import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2, Save, Trash2, Plus, Sparkles, ExternalLink, Tag, Box, Swords, FileSearch, Archive, ArchiveRestore, MoreHorizontal } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { TagInput } from "@/components/TagInput";
import { ChipListEditor } from "@/components/ChipListEditor";
import { CollapsibleSection } from "@/components/CollapsibleSection";
import { LanguageMultiSelect, CountryMultiSelect } from "@/components/LocalePickers";
import { AutofillModal } from "@/components/AutofillModal";
import { SectionAutofillButton } from "@/components/SectionAutofillButton";
import { Switch } from "@/components/ui/switch";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const WRITE_ROLES = ["owner", "admin", "researcher"];
const RELATION_TYPES = ["direct", "indirect", "aspirational"];

export default function BrandDetail() {
  const { t } = useTranslation(["brands", "products", "competitors", "common", "errors", "autofill"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);

  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Linked items
  const [products, setProducts] = useState([]);
  const [competitors, setCompetitors] = useState([]);
  const [showArchivedProducts, setShowArchivedProducts] = useState(false);

  // Sub-modals
  const [openProductAutofill, setOpenProductAutofill] = useState(false);
  const [openProductCreate, setOpenProductCreate] = useState(false);
  const [openCompetitorCreate, setOpenCompetitorCreate] = useState(false);
  const [openCompetitorSuggest, setOpenCompetitorSuggest] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bRes, pRes, cRes] = await Promise.all([
        api.get(`/brands/${id}`),
        api.get(`/brands/${id}/products?include_archived=true`),
        api.get(`/brands/${id}/competitors`),
      ]);
      setBrand(bRes.data);
      setProducts(pRes.data);
      setCompetitors(cRes.data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(t("errors:notFound"));
        navigate("/brands");
        return;
      }
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t]);

  useEffect(() => {
    load();
  }, [load]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!brand) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/brands/${brand.id}`, {
        name: brand.name?.trim(),
        url: brand.url || null,
        industry: brand.industry || null,
        positioning: brand.positioning || null,
        audience: brand.audience || null,
        languages: brand.languages || [],
        countries: brand.countries || [],
        // Phase 7 strategic fields
        tagline: brand.tagline || null,
        mission: brand.mission || null,
        vision: brand.vision || null,
        brand_story: brand.brand_story || null,
        brand_values: brand.brand_values || [],
        brand_voice: brand.brand_voice || null,
        key_messages: brand.key_messages || [],
        differentiators: brand.differentiators || [],
        proof_points: brand.proof_points || [],
      });
      setBrand(data);
      toast.success(t("brands:detail.saveSuccess"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setSaving(false);
    }
  };

  const [archiveBusy, setArchiveBusy] = useState(false);
  const isArchived = !!brand?.archived_at;
  const onArchive = async () => {
    if (!brand) return;
    setArchiveBusy(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/archive`);
      setBrand({ ...brand, archived_at: data.archived_at });
      toast.success(t("brands:detail.archived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setArchiveBusy(false); }
  };
  const onUnarchive = async () => {
    if (!brand) return;
    setArchiveBusy(true);
    try {
      await api.post(`/brands/${brand.id}/unarchive`);
      setBrand({ ...brand, archived_at: null });
      toast.success(t("brands:detail.unarchived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setArchiveBusy(false); }
  };

  // Phase 7 — archive/unarchive a single product from the Products tab.
  const onArchiveProduct = async (p) => {
    try {
      const { data } = await api.post(`/products/${p.id}/archive`);
      setProducts((prev) => prev.map((row) => row.id === p.id ? { ...row, archived_at: data.archived_at } : row));
      toast.success(t("products:detail.archived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };
  const onUnarchiveProduct = async (p) => {
    try {
      await api.post(`/products/${p.id}/unarchive`);
      setProducts((prev) => prev.map((row) => row.id === p.id ? { ...row, archived_at: null } : row));
      toast.success(t("products:detail.unarchived"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    }
  };

  const visibleProducts = useMemo(
    () => (showArchivedProducts ? products : products.filter((p) => !p.archived_at)),
    [products, showArchivedProducts]
  );

  // Single-competitor delete confirmation (Phase 7 #3 + #6).
  const [pendingCompetitorDelete, setPendingCompetitorDelete] = useState(null);
  const [competitorDeleting, setCompetitorDeleting] = useState(false);
  const confirmDeleteCompetitor = async () => {
    if (!pendingCompetitorDelete) return;
    setCompetitorDeleting(true);
    try {
      await api.delete(`/competitors/${pendingCompetitorDelete.id}`);
      setCompetitors((prev) => prev.filter((c) => c.id !== pendingCompetitorDelete.id));
      toast.success(t("competitors:deleteSuccess"));
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally { setCompetitorDeleting(false); setPendingCompetitorDelete(null); }
  };

  const onDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/brands/${brand.id}`);
      toast.success(t("brands:detail.deleted"));
      navigate("/brands");
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const onProductAutofillSuccess = ({ draft, source_url }) => {
    setOpenProductAutofill(false);
    navigate(`/brands/${brand.id}/products/new/review`, { state: { draft, source_url, brand_id: brand.id, brand_name: brand.name } });
  };

  if (loading || !brand) {
    return (
      <div className="flex items-center justify-center py-20 text-sm text-slate-500">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {t("common:loading")}
      </div>
    );
  }

  return (
    <div className={`space-y-8 ${isArchived ? "opacity-90" : ""}`} data-testid="brand-detail-page">
      {isArchived && (
        <div className="flex items-center justify-between gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3" data-testid="brand-archive-banner">
          <div className="flex items-start gap-2">
            <Archive className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" strokeWidth={1.75} />
            <div>
              <p className="font-display text-sm font-semibold text-amber-900">{t("brands:detail.archivedTitle")}</p>
              <p className="text-[11px] text-amber-800/80">{t("brands:detail.archivedHint")}</p>
            </div>
          </div>
          {canWrite && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onUnarchive}
              disabled={archiveBusy}
              data-testid="brand-unarchive-button"
              className="border-amber-300 bg-white text-amber-900 hover:bg-amber-100"
            >
              {archiveBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><ArchiveRestore className="mr-1 h-3.5 w-3.5" strokeWidth={1.75} /> {t("brands:detail.unarchiveAction")}</>}
            </Button>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-4">
        <Link
          to="/brands"
          data-testid="brand-back"
          className="inline-flex items-center gap-1 text-sm font-medium text-slate-500 transition-colors hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
          {t("brands:detail.backToList")}
        </Link>
        {brand.autofill_source && (
          <Badge variant="outline" className="border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-indigo-700">
            <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("autofill:aiBadge")}
          </Badge>
        )}
      </div>

      <header className="space-y-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">
          <Tag className="mr-1 inline h-3 w-3" strokeWidth={2} /> {brand.industry || "—"}
        </span>
        <h1 className="font-display text-3xl font-semibold tracking-tight text-slate-900">{brand.name}</h1>
        {brand.url && (
          <a href={brand.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-600">
            {brand.url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3.5 w-3.5" strokeWidth={1.75} />
          </a>
        )}
      </header>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList data-testid="brand-tabs">
          <TabsTrigger value="overview" data-testid="brand-tab-overview">{t("brands:detail.tabs.overview")}</TabsTrigger>
          <TabsTrigger value="products" data-testid="brand-tab-products">
            {t("brands:detail.tabs.products")} <span className="ml-1 text-[10px] text-slate-400">{products.length}</span>
          </TabsTrigger>
          <TabsTrigger value="competitors" data-testid="brand-tab-competitors">
            {t("brands:detail.tabs.competitors")} <span className="ml-1 text-[10px] text-slate-400">{competitors.length}</span>
          </TabsTrigger>
          <TabsTrigger value="sources" data-testid="brand-tab-sources">{t("brands:detail.tabs.sources")}</TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW: edit form ─── */}
        <TabsContent value="overview">
          <form onSubmit={onSave} className="space-y-6 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" noValidate>
            <BrandFormFields brand={brand} setBrand={setBrand} disabled={!canWrite} canWrite={canWrite} />
            {canWrite && (
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-6">
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setConfirmDelete(true)}
                    data-testid="brand-delete-button"
                    className="border-rose-200 text-rose-700 hover:bg-rose-50"
                  >
                    <Trash2 className="mr-1 h-4 w-4" strokeWidth={1.75} />
                    {t("brands:edit.delete")}
                  </Button>
                  {!isArchived && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={onArchive}
                      disabled={archiveBusy}
                      data-testid="brand-archive-button"
                      className="border-slate-200 text-slate-700 hover:bg-slate-50"
                    >
                      {archiveBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("brands:detail.archiveAction")}</>}
                    </Button>
                  )}
                </div>
                <Button type="submit" disabled={saving} data-testid="brand-save-button" className="bg-slate-900 text-white hover:bg-slate-800">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" strokeWidth={1.75} /> {t("common:save")}</>}
                </Button>
              </div>
            )}
          </form>
        </TabsContent>

        {/* ─── PRODUCTS ─── */}
        <TabsContent value="products">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              {canWrite ? (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setOpenProductCreate(true)}
                    data-testid="brand-new-product-manual"
                    className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                  >
                    <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:products.newProduct")}
                  </Button>
                  <Button
                    onClick={() => setOpenProductAutofill(true)}
                    data-testid="brand-new-product-from-url"
                    className="bg-slate-900 text-white hover:bg-slate-800"
                  >
                    <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:products.newFromUrl")}
                  </Button>
                </div>
              ) : <span />}
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5">
                <Switch
                  id="brand-products-show-archived"
                  checked={showArchivedProducts}
                  onCheckedChange={setShowArchivedProducts}
                  data-testid="brand-products-show-archived-toggle"
                />
                <Label htmlFor="brand-products-show-archived" className="cursor-pointer text-xs text-slate-600">
                  {t("products:list.showArchived")}
                </Label>
              </div>
            </div>
            {visibleProducts.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid="brand-no-products">
                {t("brands:detail.noProducts")}
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {visibleProducts.map((p) => {
                  const archived = !!p.archived_at;
                  return (
                    <div
                      key={p.id}
                      data-testid={`brand-product-${p.id}`}
                      className={`group relative flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4 transition-shadow hover:shadow-md ${archived ? "opacity-60" : ""}`}
                    >
                      <Link to={`/products/${p.id}`} className="flex min-w-0 flex-1 flex-col gap-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <Box className="h-4 w-4 shrink-0 text-indigo-600" strokeWidth={1.75} />
                          <span className="font-display text-sm font-semibold text-slate-900">{p.name}</span>
                          {archived && (
                            <Badge variant="outline" data-testid={`brand-product-archived-badge-${p.id}`} className="border-amber-200 bg-amber-50 text-[10px] font-semibold text-amber-800">
                              <Archive className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("products:list.archivedBadge")}
                            </Badge>
                          )}
                        </div>
                        {p.category && <span className="text-xs text-slate-500">{p.category}</span>}
                        {p.autofill_source && (
                          <Badge variant="outline" className="w-fit border-indigo-200 bg-indigo-50 text-[10px] text-indigo-700">
                            <Sparkles className="mr-1 h-2.5 w-2.5" strokeWidth={2} /> {t("autofill:aiBadge")}
                          </Badge>
                        )}
                      </Link>
                      {canWrite && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`brand-product-menu-${p.id}`}
                              className="h-8 px-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                            >
                              <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-44">
                            {archived ? (
                              <DropdownMenuItem
                                data-testid={`brand-product-menu-unarchive-${p.id}`}
                                onClick={() => onUnarchiveProduct(p)}
                              >
                                <ArchiveRestore className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("products:detail.unarchiveAction")}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem
                                data-testid={`brand-product-menu-archive-${p.id}`}
                                onClick={() => onArchiveProduct(p)}
                              >
                                <Archive className="mr-2 h-4 w-4" strokeWidth={1.75} /> {t("products:detail.archiveAction")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* ─── COMPETITORS ─── */}
        <TabsContent value="competitors">
          <div className="space-y-4">
            {canWrite && (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setOpenCompetitorCreate(true)}
                  data-testid="brand-new-competitor-manual"
                  className="border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                >
                  <Plus className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:competitors.addManual")}
                </Button>
                <Button
                  onClick={() => setOpenCompetitorSuggest(true)}
                  data-testid="brand-suggest-competitors"
                  className="bg-slate-900 text-white hover:bg-slate-800"
                >
                  <Sparkles className="mr-1 h-4 w-4" strokeWidth={2} /> {t("brands:competitors.suggestAi")}
                </Button>
              </div>
            )}
            {competitors.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500" data-testid="brand-no-competitors">
                {t("brands:detail.noCompetitors")}
              </div>
            ) : (
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <CompetitorTable competitors={competitors} canWrite={canWrite} onDelete={setPendingCompetitorDelete} />
              </div>
            )}
          </div>
        </TabsContent>

        {/* ─── SOURCES ─── */}
        <TabsContent value="sources">
          {brand.autofill_source?.raw_pages?.length ? (
            <div className="space-y-2">
              {brand.autofill_source.raw_pages.map((p, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                  <FileSearch className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" strokeWidth={1.75} />
                  <div className="min-w-0 flex-1">
                    <a href={p.url} target="_blank" rel="noreferrer" className="block truncate text-sm font-medium text-indigo-700 hover:text-indigo-900">
                      {p.title || p.url}
                    </a>
                    {p.excerpt && <p className="mt-1 text-xs text-slate-500">{p.excerpt}</p>}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-white px-6 py-10 text-center text-sm text-slate-500">
              {t("brands:detail.noSources")}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* ─── Modals ─── */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent data-testid="brand-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("brands:edit.delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("brands:edit.confirmDelete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="brand-confirm-delete-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} disabled={deleting} data-testid="brand-confirm-delete-confirm" className="bg-rose-600 text-white hover:bg-rose-700">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("brands:edit.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AutofillModal open={openProductAutofill} onOpenChange={setOpenProductAutofill} mode="product" brandId={brand.id} brandName={brand.name} onSuccess={onProductAutofillSuccess} />

      <CreateProductModal open={openProductCreate} onOpenChange={setOpenProductCreate} brand={brand} onCreated={(p) => { setProducts([p, ...products]); }} />
      <CreateCompetitorModal open={openCompetitorCreate} onOpenChange={setOpenCompetitorCreate} brand={brand} onCreated={(c) => { setCompetitors([c, ...competitors]); }} />
      <SuggestCompetitorsModal open={openCompetitorSuggest} onOpenChange={setOpenCompetitorSuggest} brand={brand} onAdded={(added) => { setCompetitors([...added, ...competitors]); }} />

      {/* Phase 7 — Competitor delete confirm (per-row trash icon on the brand Competitors tab) */}
      <AlertDialog open={!!pendingCompetitorDelete} onOpenChange={(o) => { if (!o) setPendingCompetitorDelete(null); }}>
        <AlertDialogContent data-testid="competitor-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("competitors:edit.delete")}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingCompetitorDelete ? t("competitors:deleteConfirm", { name: pendingCompetitorDelete.name }) : ""}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="competitor-confirm-delete-cancel">{t("common:cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteCompetitor}
              disabled={competitorDeleting}
              data-testid="competitor-confirm-delete-confirm"
              className="bg-rose-600 text-white hover:bg-rose-700"
            >
              {competitorDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("competitors:edit.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─── Brand form fields: Phase 7 collapsible sections (Identity / Story /
//     Voice & messaging / Strategy / Proof) + Phase 9 section-scoped Fill with AI ───
function BrandFormFields({ brand, setBrand, disabled, canWrite }) {
  const { t } = useTranslation("brands");
  const setVoice = (patch) => setBrand({ ...brand, brand_voice: { ...(brand.brand_voice || {}), ...patch } });
  const voice = brand.brand_voice || {};
  const hasCachedSources = !!(brand.autofill_source?.raw_pages?.length);
  // Merge a section-scoped AI draft into local brand state. We drop null/empty
  // values so the AI can't blank out a user's intentional manual entry by
  // returning ``null`` for a field outside the focused section.
  const mergeDraft = (draft) => {
    if (!draft || typeof draft !== "object") return;
    const patch = {};
    for (const [k, v] of Object.entries(draft)) {
      if (v === null || v === undefined) continue;
      if (Array.isArray(v) && v.length === 0) continue;
      patch[k] = v;
    }
    setBrand((prev) => ({ ...(prev || brand), ...patch }));
  };
  const aiFill = (sectionKey, sectionLabel) =>
    canWrite ? (
      <SectionAutofillButton
        entityType="brand"
        entityId={brand.id}
        sectionKey={sectionKey}
        sectionLabel={sectionLabel}
        testIdPrefix={`brand-section-${sectionKey}`}
        hasCachedSources={hasCachedSources}
        onDraft={mergeDraft}
        disabled={disabled}
      />
    ) : null;
  return (
    <div className="space-y-5">
      {/* IDENTITY */}
      <CollapsibleSection
        testId="brand-section-identity"
        title={t("sections.identity.title")}
        subtitle={t("sections.identity.subtitle")}
        aiFill={aiFill("identity", t("sections.identity.title"))}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label={t("form.nameLabel")} required>
            <Input required disabled={disabled} value={brand.name || ""}
                   onChange={(e) => setBrand({ ...brand, name: e.target.value })}
                   data-testid="brand-edit-name" className="h-11" />
          </Field>
          <Field label={t("form.urlLabel")}>
            <Input type="url" disabled={disabled} value={brand.url || ""}
                   onChange={(e) => setBrand({ ...brand, url: e.target.value })}
                   data-testid="brand-edit-url" className="h-11" />
          </Field>
          <Field label={t("form.industryLabel")}>
            <Input disabled={disabled} value={brand.industry || ""}
                   onChange={(e) => setBrand({ ...brand, industry: e.target.value })}
                   data-testid="brand-edit-industry" className="h-11" />
          </Field>
          <Field label={t("form.taglineLabel")} hint={t("form.taglineHint")}>
            <Input disabled={disabled} value={brand.tagline || ""}
                   onChange={(e) => setBrand({ ...brand, tagline: e.target.value })}
                   data-testid="brand-edit-tagline" className="h-11" />
          </Field>
          <Field label={t("form.languagesLabel")} hint={t("form.languagesHint")}>
            <LanguageMultiSelect value={brand.languages || []}
              onChange={(v) => setBrand({ ...brand, languages: v })}
              dataTestid="brand-edit-languages" />
          </Field>
          <Field label={t("form.countriesLabel")} hint={t("form.countriesHint")}>
            <CountryMultiSelect value={brand.countries || []}
              onChange={(v) => setBrand({ ...brand, countries: v })}
              dataTestid="brand-edit-countries" />
          </Field>
        </div>
      </CollapsibleSection>

      {/* STORY */}
      <CollapsibleSection
        testId="brand-section-story"
        title={t("sections.story.title")}
        subtitle={t("sections.story.subtitle")}
        aiFill={aiFill("story", t("sections.story.title"))}
      >
        <div className="space-y-4">
          <Field label={t("form.missionLabel")} hint={t("form.missionHint")}>
            <Textarea disabled={disabled} value={brand.mission || ""}
              onChange={(e) => setBrand({ ...brand, mission: e.target.value })}
              data-testid="brand-edit-mission" rows={4} />
          </Field>
          <Field label={t("form.visionLabel")} hint={t("form.visionHint")}>
            <Textarea disabled={disabled} value={brand.vision || ""}
              onChange={(e) => setBrand({ ...brand, vision: e.target.value })}
              data-testid="brand-edit-vision" rows={4} />
          </Field>
          <Field label={t("form.brandStoryLabel")} hint={t("form.brandStoryHint")}>
            <Textarea disabled={disabled} value={brand.brand_story || ""}
              onChange={(e) => setBrand({ ...brand, brand_story: e.target.value })}
              data-testid="brand-edit-brand-story" rows={6} />
          </Field>
        </div>
      </CollapsibleSection>

      {/* VOICE & MESSAGING */}
      <CollapsibleSection
        testId="brand-section-voice"
        title={t("sections.voice.title")}
        subtitle={t("sections.voice.subtitle")}
        aiFill={aiFill("voice_messaging", t("sections.voice.title"))}
      >
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={t("form.voiceToneLabel")}>
              <Input disabled={disabled} value={voice.tone || ""}
                onChange={(e) => setVoice({ tone: e.target.value })}
                data-testid="brand-edit-voice-tone" className="h-11" />
            </Field>
            <Field label={t("form.voiceStyleLabel")}>
              <Input disabled={disabled} value={voice.style || ""}
                onChange={(e) => setVoice({ style: e.target.value })}
                data-testid="brand-edit-voice-style" className="h-11" />
            </Field>
          </div>
          <Field label={t("form.voiceDoLabel")} hint={t("form.voiceDoHint")}>
            <ChipListEditor disabled={disabled} value={voice.do || []}
              onChange={(v) => setVoice({ do: v })}
              testId="brand-edit-voice-do"
              placeholder={t("form.voiceDoPlaceholder")} />
          </Field>
          <Field label={t("form.voiceDontLabel")} hint={t("form.voiceDontHint")}>
            <ChipListEditor disabled={disabled} value={voice.dont || []}
              onChange={(v) => setVoice({ dont: v })}
              testId="brand-edit-voice-dont" chipTone="amber"
              placeholder={t("form.voiceDontPlaceholder")} />
          </Field>
          <Field label={t("form.voiceExamplesLabel")} hint={t("form.voiceExamplesHint")}>
            <ChipListEditor disabled={disabled} value={voice.examples || []}
              onChange={(v) => setVoice({ examples: v })}
              testId="brand-edit-voice-examples"
              placeholder={t("form.voiceExamplesPlaceholder")} />
          </Field>
          <Field label={t("form.keyMessagesLabel")} hint={t("form.keyMessagesHint")}>
            <ChipListEditor disabled={disabled} value={brand.key_messages || []}
              onChange={(v) => setBrand({ ...brand, key_messages: v })}
              testId="brand-edit-key-messages"
              placeholder={t("form.keyMessagesPlaceholder")} />
          </Field>
          <Field label={t("form.differentiatorsLabel")} hint={t("form.differentiatorsHint")}>
            <ChipListEditor disabled={disabled} value={brand.differentiators || []}
              onChange={(v) => setBrand({ ...brand, differentiators: v })}
              testId="brand-edit-differentiators"
              placeholder={t("form.differentiatorsPlaceholder")} />
          </Field>
        </div>
      </CollapsibleSection>

      {/* STRATEGY */}
      <CollapsibleSection
        testId="brand-section-strategy"
        title={t("sections.strategy.title")}
        subtitle={t("sections.strategy.subtitle")}
        aiFill={aiFill("strategy", t("sections.strategy.title"))}
      >
        <div className="space-y-4">
          <Field label={t("form.positioningLabel")} hint={t("form.positioningHint")}>
            <Textarea disabled={disabled} value={brand.positioning || ""}
              onChange={(e) => setBrand({ ...brand, positioning: e.target.value })}
              data-testid="brand-edit-positioning" rows={5} />
          </Field>
          <Field label={t("form.audienceLabel")} hint={t("form.audienceHint")}>
            <Textarea disabled={disabled} value={brand.audience || ""}
              onChange={(e) => setBrand({ ...brand, audience: e.target.value })}
              data-testid="brand-edit-audience" rows={5} />
          </Field>
        </div>
      </CollapsibleSection>

      {/* PROOF */}
      <CollapsibleSection
        testId="brand-section-proof"
        title={t("sections.proof.title")}
        subtitle={t("sections.proof.subtitle")}
        aiFill={aiFill("proof", t("sections.proof.title"))}
      >
        <div className="space-y-4">
          <Field label={t("form.brandValuesLabel")} hint={t("form.brandValuesHint")}>
            <ChipListEditor disabled={disabled} value={brand.brand_values || []}
              onChange={(v) => setBrand({ ...brand, brand_values: v })}
              testId="brand-edit-brand-values"
              placeholder={t("form.brandValuesPlaceholder")} />
          </Field>
          <Field label={t("form.proofPointsLabel")} hint={t("form.proofPointsHint")}>
            <ChipListEditor disabled={disabled} value={brand.proof_points || []}
              onChange={(v) => setBrand({ ...brand, proof_points: v })}
              testId="brand-edit-proof-points"
              placeholder={t("form.proofPointsPlaceholder")} />
          </Field>
        </div>
      </CollapsibleSection>
    </div>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {label}
        {required ? <span className="ml-0.5 text-rose-500">*</span> : null}
      </Label>
      {children}
      {hint ? <p className="text-[11px] text-slate-400">{hint}</p> : null}
    </div>
  );
}

// ─── Inline: competitor table (Phase 7: scope badge + delete button) ───
function CompetitorTable({ competitors, onDelete, canWrite }) {
  const { t } = useTranslation("competitors");
  return (
    <table className="w-full text-sm">
      <thead className="bg-slate-50">
        <tr className="border-b border-slate-200">
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.name")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.relation")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.confidence")}</th>
          <th className="px-6 py-3 text-left text-[11px] uppercase tracking-wider text-slate-500">{t("list.columns.discovered")}</th>
          <th className="px-6 py-3 text-right text-[11px] uppercase tracking-wider text-slate-500" aria-label="actions"></th>
        </tr>
      </thead>
      <tbody>
        {competitors.map((c) => (
          <tr key={c.id} data-testid={`competitor-row-${c.id}`} className="border-t border-slate-100">
            <td className="px-6 py-4">
              <div className="flex flex-col">
                <span className="font-medium text-slate-900">{c.name}</span>
                {c.url && (
                  <a href={c.url} target="_blank" rel="noreferrer" className="text-[11px] text-slate-500 hover:text-indigo-600">
                    {c.url.replace(/^https?:\/\//, "")}
                  </a>
                )}
                {c.scope === "product" && (
                  <Badge variant="outline" className="mt-1 w-fit border-indigo-200 bg-indigo-50 text-[10px] text-indigo-700"
                         data-testid={`competitor-scope-${c.id}`}>
                    {t("scope.product")}
                  </Badge>
                )}
              </div>
            </td>
            <td className="px-6 py-4 text-slate-600">{t(`relation.${c.relation_type}`)}</td>
            <td className="px-6 py-4 text-slate-600">{Math.round((c.confidence || 0) * 100)}%</td>
            <td className="px-6 py-4">
              <Badge variant="outline" className={c.discovered_by === "ai_suggestion" ? "border-indigo-200 bg-indigo-50 text-indigo-700" : "border-slate-200 bg-slate-50 text-slate-700"}>
                {t(`discovered.${c.discovered_by}`)}
              </Badge>
            </td>
            <td className="px-6 py-4 text-right">
              {canWrite && onDelete ? (
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  data-testid={`competitor-delete-${c.id}`}
                  onClick={() => onDelete(c)}
                  className="h-8 px-2 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                >
                  <Trash2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </Button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─── Inline: create product modal ───
function CreateProductModal({ open, onOpenChange, brand, onCreated }) {
  const { t } = useTranslation(["products", "common", "errors"]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!open) { setName(""); setCategory(""); setUrl(""); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/products`, { name: name.trim(), category: category.trim() || null, url: url.trim() || null });
      toast.success(t("products:detail.saveSuccess"));
      onCreated(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-product-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">{t("products:create.title")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("products:create.subtitle", { brand: brand?.name })}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.nameLabel")}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("products:form.namePlaceholder")} data-testid="create-product-name" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.categoryLabel")}</Label>
            <Input value={category} onChange={(e) => setCategory(e.target.value)} placeholder={t("products:form.categoryPlaceholder")} data-testid="create-product-category" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("products:form.urlLabel")}</Label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("products:form.urlPlaceholder")} data-testid="create-product-url" className="h-11" />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="create-product-cancel">{t("common:cancel")}</Button>
            <Button type="submit" disabled={submitting} data-testid="create-product-submit" className="bg-slate-900 text-white hover:bg-slate-800">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("products:create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Inline: create competitor modal ───
function CreateCompetitorModal({ open, onOpenChange, brand, onCreated }) {
  const { t } = useTranslation(["competitors", "common", "errors"]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [relation, setRelation] = useState("direct");
  const [confidence, setConfidence] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => { if (!open) { setName(""); setUrl(""); setRelation("direct"); setConfidence(1); } }, [open]);
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post(`/brands/${brand.id}/competitors`, { name: name.trim(), url: url.trim() || null, relation_type: relation, confidence: Number(confidence) });
      toast.success(t("competitors:edit.saveSuccess"));
      onCreated(data);
      onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err, t("errors:generic"))); } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="create-competitor-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-semibold tracking-tight">{t("competitors:create.title")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("competitors:create.subtitle", { brand: brand?.name })}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.nameLabel")}</Label>
            <Input required value={name} onChange={(e) => setName(e.target.value)} placeholder={t("competitors:form.namePlaceholder")} data-testid="create-competitor-name" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.urlLabel")}</Label>
            <Input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={t("competitors:form.urlPlaceholder")} data-testid="create-competitor-url" className="h-11" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.relationLabel")}</Label>
              <Select value={relation} onValueChange={setRelation}>
                <SelectTrigger data-testid="create-competitor-relation" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {RELATION_TYPES.map((r) => (
                    <SelectItem key={r} value={r}>{t(`competitors:relation.${r}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("competitors:form.confidenceLabel")}</Label>
              <Input type="number" min="0" max="1" step="0.1" value={confidence} onChange={(e) => setConfidence(e.target.value)} data-testid="create-competitor-confidence" className="h-11" />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="create-competitor-cancel">{t("common:cancel")}</Button>
            <Button type="submit" disabled={submitting} data-testid="create-competitor-submit" className="bg-slate-900 text-white hover:bg-slate-800">
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("competitors:create.submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Inline: AI-suggest competitors modal ───
function SuggestCompetitorsModal({ open, onOpenChange, brand, onAdded }) {
  const { t } = useTranslation(["brands", "competitors", "common", "errors", "autofill"]);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [picked, setPicked] = useState(new Set());
  const [errorKey, setErrorKey] = useState(null);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!open) { setSuggestions([]); setPicked(new Set()); setErrorKey(null); return; }
    setLoading(true);
    setErrorKey(null);
    api.post("/autofill/competitors", { brand_id: brand.id, max: 8 })
      .then(({ data }) => {
        setSuggestions(data?.competitors || []);
        setPicked(new Set((data?.competitors || []).map((_, i) => i)));
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail || "";
        if (typeof detail === "string" && detail) setErrorKey(detail);
        else setErrorKey("generic");
      })
      .finally(() => setLoading(false));
  }, [open, brand?.id]);

  const togglePick = (i) => {
    const next = new Set(picked);
    if (next.has(i)) next.delete(i); else next.add(i);
    setPicked(next);
  };

  const save = async () => {
    setAdding(true);
    try {
      const toCreate = suggestions.filter((_, i) => picked.has(i));
      const added = [];
      for (const s of toCreate) {
        const { data } = await api.post(`/brands/${brand.id}/competitors`, {
          name: s.name,
          url: s.url || null,
          relation_type: s.relation_type || "direct",
          confidence: s.confidence ?? 0.5,
        });
        added.push({ ...data, discovered_by: "ai_suggestion" });
      }
      toast.success(t("brands:competitors.suggestions.added", { count: added.length }));
      onAdded(added);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, t("errors:generic")));
    } finally {
      setAdding(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="suggest-competitors-modal" className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl font-semibold tracking-tight">
            <Sparkles className="h-4 w-4 text-indigo-600" strokeWidth={2} />
            {t("brands:competitors.suggestions.title")}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("brands:competitors.suggestions.subtitle")}</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("common:loading")}</div>
        ) : errorKey === "llm_failed" ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
            <p className="font-display text-sm font-semibold text-rose-900">{t("errors:llm_failed.title")}</p>
            <p className="mt-1 text-xs text-rose-800/80">{t("errors:llm_failed.body")}</p>
          </div>
        ) : suggestions.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            {t("brands:competitors.suggestions.noResults")}
          </p>
        ) : (
          <div className="max-h-[50vh] space-y-2 overflow-y-auto">
            {suggestions.map((s, i) => (
              <label
                key={i}
                data-testid={`suggested-competitor-${i}`}
                className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${picked.has(i) ? "border-indigo-300 bg-indigo-50/50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              >
                <input type="checkbox" checked={picked.has(i)} onChange={() => togglePick(i)} className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-baseline gap-x-3">
                    <span className="font-display text-sm font-semibold text-slate-900">{s.name}</span>
                    <span className="text-[11px] text-slate-500">{t(`competitors:relation.${s.relation_type || "direct"}`)}</span>
                    <span className="text-[11px] text-slate-400">{t("brands:competitors.suggestions.confidence")}: {Math.round((s.confidence || 0) * 100)}%</span>
                  </div>
                  {s.url && <p className="mt-0.5 truncate text-[11px] text-slate-500">{s.url.replace(/^https?:\/\//, "")}</p>}
                  {s.reason && <p className="mt-1 text-xs text-slate-500">{s.reason}</p>}
                </div>
              </label>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} data-testid="suggest-competitors-cancel">{t("common:cancel")}</Button>
          {!errorKey && suggestions.length > 0 && (
            <Button type="button" onClick={save} disabled={adding || picked.size === 0} data-testid="suggest-competitors-save" className="bg-slate-900 text-white hover:bg-slate-800">
              {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1 h-4 w-4" strokeWidth={2} />{t("brands:competitors.suggestions.include")} ({picked.size})</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
